// Display-only helpers for Command Center rows and cards.
// These are stable-per-item but NOT persisted — purely presentation.

/**
 * Hash a UUID (or any string id) to a zero-padded 3-digit display number,
 * then format as "T-###". Deterministic — same id always produces same output.
 *
 * Collisions are possible with 1000 buckets but acceptable for at-a-glance
 * display. Source-of-truth remains the full `id`.
 */
export function formatTaskDisplayId(id: string): string {
  // FNV-1a 32-bit hash, simple and fast
  let hash = 0x811c9dc5;
  for (let i = 0; i < id.length; i++) {
    hash ^= id.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  const bucket = Math.abs(hash) % 1000;
  return `T-${bucket.toString().padStart(3, "0")}`;
}

const MONTH_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const MS_PER_HOUR = 60 * 60 * 1000;
const MS_PER_DAY = 24 * MS_PER_HOUR;
const WARN_THRESHOLD_DAYS = 2; // due within 2 days → amber
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export interface FormattedDueDate {
  readonly label: string;
  /** True when the due date is overdue or within WARN_THRESHOLD_DAYS */
  readonly warn: boolean;
  /** True when the due date is in the past */
  readonly overdue: boolean;
}

/**
 * Format an ISO due date string as "Apr 18" (this year) or "Apr 18 '25" (prior year).
 * Returns warn/overdue flags for conditional styling in the row cell.
 */
export function formatDueDate(
  dueDate: string | null | undefined,
  now: Date = new Date(),
): FormattedDueDate | null {
  const dateValue = formatDateInputValue(dueDate);
  if (!dateValue) return null;

  const parsed = parseDateOnlyUtc(dateValue);
  const month = MONTH_SHORT[parsed.getUTCMonth()] ?? "";
  const day = parsed.getUTCDate();
  const yearSuffix =
    parsed.getUTCFullYear() === now.getFullYear()
      ? ""
      : ` '${String(parsed.getUTCFullYear()).slice(-2)}`;
  const label = `${month} ${day}${yearSuffix}`;

  const diffMs = parsed.getTime() - now.getTime();
  const overdue = diffMs < 0;
  const warn = overdue || diffMs < WARN_THRESHOLD_DAYS * MS_PER_DAY;

  return { label, warn, overdue };
}

export function formatDateInputValue(dueDate: string | null | undefined): string {
  if (!dueDate) return "";

  const trimmed = dueDate.trim();
  if (DATE_ONLY_PATTERN.test(trimmed)) return trimmed;

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return "";

  return [
    String(parsed.getUTCFullYear()).padStart(4, "0"),
    String(parsed.getUTCMonth() + 1).padStart(2, "0"),
    String(parsed.getUTCDate()).padStart(2, "0"),
  ].join("-");
}

function parseDateOnlyUtc(value: string): Date {
  const [year = "0", month = "1", day = "1"] = value.split("-");
  return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
}
